/**
 * Who signs what, and what gets locked when they do.
 *
 * A **step** is the unit the engine works in: its merged values are pinned the
 * moment somebody signs it, a later step cannot be filled while an earlier one
 * is still waiting, and a signed step is refused a write from every direction —
 * the document, the grid form, business logic and the offline replay alike. On
 * a composed document a SECTION is a step, which is how a batch record is
 * signed off in pieces over a fortnight.
 *
 * None of that could be authored before this tab existed. The engine has read
 * `steps[]` since the feature was built; nothing in the designer ever wrote
 * one, so a form's sign-off could only be produced by hand-editing JSON. That
 * is the gap this closes, and it is the thing that makes a batch record a
 * batch record rather than a long form.
 *
 * Three rules the screen states rather than assumes, because each of them
 * surprises people:
 *
 * - **Order decides locking.** Step 2 waits for step 1's required signatures.
 * - **The same order number means PARALLEL.** Two lines a QA officer and a
 *   production officer may sign in either order share an order number; two
 *   that must go in sequence do not.
 * - **A field with no step belongs to the document**, not to the first step.
 *   It stays editable after that step is signed — which is right, and is why
 *   the field list below shows what is unassigned rather than hiding it.
 */
import { useMemo } from 'react'

import type { FieldRow, FormModel, FormTemplate } from './types'
import { buttonStyle, inputStyle } from './styles'

type Step = NonNullable<FormTemplate['steps']>[number]
type Signature = NonNullable<Step['signatures']>[number]

/** What a signature MEANS — the Part 11 vocabulary, not free text. */
const MEANINGS: { value: string; label: string; hint: string }[] = [
  { value: 'performance', label: 'Performed by',
    hint: 'I did this work' },
  { value: 'review', label: 'Reviewed by',
    hint: 'I have read it and it is complete' },
  { value: 'approval', label: 'Approved by',
    hint: 'I accept it on the organisation’s behalf' },
  { value: 'verification', label: 'Verified by',
    hint: 'I independently checked the figures' },
  { value: 'authorship', label: 'Authored by',
    hint: 'I wrote what it says' },
  { value: 'witness', label: 'Witnessed by',
    hint: 'I watched it being done' },
]

function slug(value: string): string {
  return value.replace(/[^A-Za-z0-9_]+/g, '_').replace(/^_+|_+$/g, '')
    .toUpperCase().slice(0, 60)
}

export default function SignOffTab({ template, templateIndex, model, mutate }: {
  template: FormTemplate
  templateIndex: number
  model: FormModel
  mutate: (fn: (m: FormModel) => void) => void
}) {
  const steps = template.steps || []
  const fields = useMemo(
    () => (template.fields || []).filter(
      (f) => f.key && f.kind !== 'signature'),
    [template.fields])

  const patchTemplate = (fn: (t: FormTemplate) => void) =>
    mutate((m) => { fn(m.form.templates[templateIndex]) })

  const patchStep = (index: number, fn: (s: Step) => void) =>
    patchTemplate((t) => {
      t.steps = t.steps || []
      if (t.steps[index]) fn(t.steps[index])
    })

  const addStep = () => patchTemplate((t) => {
    t.steps = t.steps || []
    let n = t.steps.length + 1
    while (t.steps.some((s) => s.key === `STEP_${n}`)) n += 1
    t.steps.push({
      key: `STEP_${n}`, title: `Step ${n}`, ordinal: t.steps.length + 1,
      signatures: [],
    })
  })

  const removeStep = (index: number) => patchTemplate((t) => {
    const gone = (t.steps || [])[index]
    t.steps = (t.steps || []).filter((_, i) => i !== index)
    // …and release every field that named it. A field pointing at a step that
    // no longer exists belongs to the document as a whole, which is what an
    // empty `step` already means — but leaving the stale name would read as an
    // assignment somebody made.
    if (gone) {
      for (const f of t.fields || []) {
        if (f.step === gone.key) {
          f.step = ''
        }
      }
    }
  })

  const moveStep = (index: number, by: number) => patchTemplate((t) => {
    const list = t.steps || []
    const to = index + by
    if (to < 0 || to >= list.length) return
    const [row] = list.splice(index, 1)
    list.splice(to, 0, row)
    // Renumbered from the ORDER on screen, so moving a step up actually moves
    // it up. Ordinals that disagreed with the list would make the lock order
    // differ from the order somebody is reading.
    list.forEach((s, i) => { s.ordinal = i + 1 })
  })

  const setFieldStep = (fieldKey: string, stepKey: string) =>
    patchTemplate((t) => {
      const found = (t.fields || []).find((f) => f.key === fieldKey)
      if (found) found.step = stepKey
    })

  const assigned = useMemo(() => {
    const out: Record<string, FieldRow[]> = {}
    for (const f of fields) {
      const key = String(f.step || '')
      out[key] = [...(out[key] || []), f]
    }
    return out
  }, [fields])

  const isSection = (template.role || 'master') === 'section'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18,
      maxWidth: 880 }}>

      <p style={explainStyle}>
        A <strong>step</strong> is what gets frozen, locked and signed. Its
        values are pinned the moment somebody signs it; a later step cannot be
        filled while an earlier one is still waiting for a required signature;
        and once signed, the record behind it is refused a change from
        everywhere — the grid form and an integration included, not just this
        document. {isSection
          ? 'This is a section, so it is signed off on its own, while the rest '
            + 'of the document is still being filled in.'
          : 'A form that nobody signs needs none of this: leave it empty.'}
      </p>

      {steps.length === 0 && (
        <p style={emptyStyle}>
          No steps, so this {isSection ? 'section' : 'document'} is signed as a
          whole when it is closed — or not at all.
        </p>
      )}

      {steps.map((step, index) => (
        <section key={step.key} style={{
          border: '1px solid var(--color-border)', borderRadius: 6,
          padding: '12px 14px', display: 'flex', flexDirection: 'column',
          gap: 10,
        }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center',
            flexWrap: 'wrap' }}>
            <strong style={{ fontSize: '0.8625rem',
              color: 'var(--color-text-secondary)' }}>{index + 1}.</strong>
            <input
              value={step.title || ''}
              placeholder="Granulation"
              onChange={(e) => patchStep(index, (s) => {
                s.title = e.target.value
                if (!s.key || /^STEP_\d+$/.test(s.key)) {
                  s.key = slug(e.target.value) || s.key
                }
              })}
              style={{ ...inputStyle, flex: '0 0 230px' }}
            />
            <code style={codeStyle}>{step.key}</code>
            <span style={{ flex: 1 }} />
            <button type="button" style={linkStyle} disabled={index === 0}
              onClick={() => moveStep(index, -1)}>↑</button>
            <button type="button" style={linkStyle}
              disabled={index === steps.length - 1}
              onClick={() => moveStep(index, 1)}>↓</button>
            <button type="button" style={linkStyle}
              onClick={() => removeStep(index)}>Remove</button>
          </div>

          {/* ── the signature lines ─────────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={headerRowStyle}>
              <span style={{ flex: '0 0 150px' }}>Line</span>
              <span style={{ flex: '0 0 160px' }}>Means</span>
              <span style={{ flex: '0 0 130px' }}>Only this role</span>
              <span style={{ flex: '0 0 62px' }}>Order</span>
              <span style={{ flex: '0 0 74px' }}>Required</span>
              <span style={{ flex: '0 0 96px' }}>Draw</span>
            </div>
            {(step.signatures || []).map((sig, si) => (
              <SignatureRow
                key={`${step.key}-${si}`}
                sig={sig}
                onChange={(fn) => patchStep(index, (s) => {
                  const list = s.signatures || []
                  if (list[si]) fn(list[si])
                })}
                onRemove={() => patchStep(index, (s) => {
                  s.signatures = (s.signatures || [])
                    .filter((_, i) => i !== si)
                })}
              />
            ))}
            {!(step.signatures || []).length && (
              <p style={emptyStyle}>
                No signature yet, so nothing locks this step.
              </p>
            )}
            <button
              type="button"
              style={{ ...buttonStyle(false), width: 'fit-content' }}
              onClick={() => patchStep(index, (s) => {
                const n = (s.signatures || []).length
                s.signatures = [...(s.signatures || []), {
                  slot: n === 0 ? 'DONE_BY' : `SIGNED_BY_${n + 1}`,
                  title: n === 0 ? 'Performed by' : 'Signed by',
                  meaning: n === 0 ? 'performance' : 'approval',
                  role: '',
                  // The SAME order as the line above it: two lines that may be
                  // signed in either order is the common case, and making them
                  // sequential by default silently forbids it.
                  ordinal: (s.signatures || [])[n - 1]?.ordinal ?? 1,
                  required: true,
                }]
              })}
            >Add a signature line</button>
            {(step.signatures || []).length > 1 && (
              <p style={emptyStyle}>
                Lines sharing an order number may be signed in either order.
                Give one a higher number to make it wait for the other.
              </p>
            )}
          </div>

          {/* ── what this step covers ───────────────────────────────── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: '0.8225rem',
              color: 'var(--color-text-secondary)' }}>
              What gets locked when it is signed
            </span>
            <div style={chipWrapStyle}>
              {(assigned[step.key] || []).map((f) => (
                <button
                  key={f.key}
                  type="button"
                  title="Remove from this step"
                  onClick={() => setFieldStep(f.key, '')}
                  style={chipStyle(true)}
                >{f.title || f.key} ×</button>
              ))}
              {!(assigned[step.key] || []).length && (
                <span style={emptyStyle}>Nothing yet.</span>
              )}
            </div>
            <select
              value=""
              onChange={(e) => {
                if (e.target.value) setFieldStep(e.target.value, step.key)
              }}
              style={{ ...inputStyle, width: 'fit-content', minWidth: 230 }}
            >
              <option value="">— add a box to this step —</option>
              {fields
                .filter((f) => f.step
                  !== step.key)
                .map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.title || f.key}
                    {f.step
                      ? `  (now on ${f.step})`
                      : ''}
                  </option>
                ))}
            </select>
          </div>
        </section>
      ))}

      <button type="button" style={{ ...buttonStyle(false), width: 'fit-content' }}
        onClick={addStep}>Add a step</button>

      {/* ── what belongs to nobody ──────────────────────────────────── */}
      {steps.length > 0 && (assigned[''] || []).length > 0 && (
        <section>
          <h3 style={{ fontSize: '0.9625rem', margin: '0 0 3px' }}>
            On no step
          </h3>
          <p style={emptyStyle}>
            These belong to the {isSection ? 'section' : 'document'} as a whole.
            They are shown on every step somebody signs — so they are part of
            what was read — but they stay editable afterwards, which is usually
            right for a heading or a reference somebody fills in early.
          </p>
          <div style={{ ...chipWrapStyle, marginTop: 6 }}>
            {(assigned[''] || []).map((f) => (
              <span key={f.key} style={chipStyle(false)}>
                {f.title || f.key}
              </span>
            ))}
          </div>
        </section>
      )}

      {!isSection && (model.form.governance === 'none') && (
        <p style={{ ...explainStyle, borderLeftColor: '#b45309' }}>
          This form’s governance is <strong>none</strong>, so it keeps no
          execution record and nothing here can be enforced: there is no
          instance to freeze and no controlled copy to sign. Steps are worth
          declaring only at <strong>audited</strong> or above.
        </p>
      )}
    </div>
  )
}

// ── one signature line ─────────────────────────────────────────────────────

function SignatureRow({ sig, onChange, onRemove }: {
  sig: Signature
  onChange: (fn: (s: Signature) => void) => void
  onRemove: () => void
}) {
  const meaning = MEANINGS.find((m) => m.value === (sig.meaning || ''))
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center',
      flexWrap: 'wrap' }}>
      <input
        value={sig.title || ''}
        placeholder="Performed by"
        onChange={(e) => onChange((s) => {
          s.title = e.target.value
          if (!s.slot || /^SIGNED_BY_\d+$/.test(s.slot)) {
            s.slot = slug(e.target.value) || s.slot
          }
        })}
        style={{ ...inputStyle, flex: '0 0 150px' }}
        title={sig.slot}
      />
      <select
        value={sig.meaning || 'performance'}
        onChange={(e) => onChange((s) => { s.meaning = e.target.value })}
        style={{ ...inputStyle, flex: '0 0 160px' }}
        title={meaning?.hint || ''}
      >
        {MEANINGS.map((m) => (
          <option key={m.value} value={m.value}>{m.label}</option>
        ))}
      </select>
      <input
        value={sig.role || ''}
        placeholder="anyone"
        onChange={(e) => onChange((s) => { s.role = e.target.value })}
        style={{ ...inputStyle, flex: '0 0 130px' }}
        title="Only somebody holding this role may sign here. Blank means anybody who can reach the document."
      />
      <input
        type="number"
        min={1}
        value={sig.ordinal ?? 1}
        onChange={(e) => onChange((s) => {
          s.ordinal = Math.max(1, Number(e.target.value) || 1)
        })}
        style={{ ...inputStyle, flex: '0 0 62px' }}
      />
      <label style={{ flex: '0 0 74px', fontSize: '0.8425rem',
        display: 'flex', alignItems: 'center', gap: 5 }}>
        <input
          type="checkbox"
          checked={sig.required !== false}
          onChange={(e) => onChange((s) => { s.required = e.target.checked })}
        />
        yes
      </label>
      <select
        value={sig.ink === true ? 'yes' : sig.ink === false ? 'no' : ''}
        onChange={(e) => onChange((s) => {
          const v = e.target.value
          if (v === 'yes') s.ink = true
          else if (v === 'no') s.ink = false
          else delete s.ink
        })}
        style={{ ...inputStyle, flex: '0 0 96px' }}
        title={'Whether the signer also DRAWS their signature on a pad. '
          + '“Default” follows the repository the document is filed into.'}
      >
        <option value="">Default</option>
        <option value="yes">Draw</option>
        <option value="no">Don’t draw</option>
      </select>
      <button type="button" style={linkStyle} onClick={onRemove}>Remove</button>
      {meaning && (
        <span style={{ ...emptyStyle, flexBasis: '100%', marginLeft: 158 }}>
          “{meaning.hint}” — printed beside the signature on the filed copy.
        </span>
      )}
    </div>
  )
}

// ── chrome ─────────────────────────────────────────────────────────────────

const explainStyle: React.CSSProperties = {
  fontSize: '0.8825rem', color: 'var(--color-text-secondary)', lineHeight: 1.55,
  margin: 0, borderLeft: '3px solid var(--color-border)', paddingLeft: 10,
}

const emptyStyle: React.CSSProperties = {
  fontSize: '0.8425rem', color: 'var(--color-text-secondary)', lineHeight: 1.5,
  margin: 0,
}

const headerRowStyle: React.CSSProperties = {
  display: 'flex', gap: 8, fontSize: '0.8125rem', letterSpacing: 0.3,
  textTransform: 'uppercase', color: 'var(--color-text-secondary)',
  flexWrap: 'wrap',
}

const codeStyle: React.CSSProperties = {
  fontSize: '0.8125rem', padding: '2px 6px', borderRadius: 4,
  background: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)',
}

const linkStyle: React.CSSProperties = {
  background: 'none', border: 'none', padding: '2px 4px',
  color: 'var(--color-accent)', cursor: 'pointer', fontSize: '0.8425rem',
}

const chipWrapStyle: React.CSSProperties = {
  display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center',
}

const chipStyle = (removable: boolean): React.CSSProperties => ({
  fontSize: '0.8125rem', padding: '2px 8px', borderRadius: 11,
  border: '1px solid var(--color-border)',
  background: removable ? 'var(--color-bg-secondary)' : 'transparent',
  color: 'var(--color-text)',
  cursor: removable ? 'pointer' : 'default',
})
